/**
 * Extract the tenant subdomain (the leading label) from the current host,
 * if and only if it matches one of the canonical DigaVouch hostname shapes:
 *
 *   <sub>.digavouch.com
 *   <sub>.uat.digavouch.com
 *   <sub>.staging.digavouch.com
 *
 * Returns null for canonical `app.<env>?.digavouch.com` (the `app` label is
 * reserved and is not a tenant), localhost, raw IPs, preview hosts
 * (*.netlify.app, *.lovable.app, *.lovableproject.com), and any non-matching
 * host. Branding falls back to "generic mode" in those cases.
 */
export function extractTenantSubdomain(hostname: string | null | undefined): string | null {
  if (!hostname) return null;
  // Strip port and any whitespace, lowercase.
  const host = hostname.toString().trim().toLowerCase().replace(/:\d+$/, "");
  if (!host) return null;

  // Quick rejects.
  if (host === "localhost" || host === "127.0.0.1" || host === "0.0.0.0") return null;
  // Raw IPv4.
  if (/^\d{1,3}(\.\d{1,3}){3}$/.test(host)) return null;
  // IPv6 literals (bracketed or unbracketed) — reject.
  if (host.includes(":") || host.startsWith("[")) return null;

  // Preview / sandbox hosts.
  if (
    host.endsWith(".netlify.app") ||
    host.endsWith(".lovable.app") ||
    host.endsWith(".lovableproject.com")
  ) {
    return null;
  }

  // Canonical DigaVouch shapes.
  const patterns: Array<RegExp> = [
    /^([a-z0-9-]+)\.staging\.digavouch\.com$/,
    /^([a-z0-9-]+)\.uat\.digavouch\.com$/,
    /^([a-z0-9-]+)\.digavouch\.com$/,
  ];

  for (const re of patterns) {
    const m = host.match(re);
    if (m) {
      const sub = m[1];
      // Reserved labels — canonical/operator hosts, NOT tenant subdomains.
      // `app` = operator dashboard; `admin` = admin /tenants surface
      // (admin.digavouch.com, admin.uat.digavouch.com).
      if (sub === "app" || sub === "admin") return null;
      // Guard against empty / single-dash subs.
      if (!sub || sub === "-" || sub.startsWith("-") || sub.endsWith("-")) return null;
      return sub;
    }
  }

  return null;
}

/**
 * Build the canonical host an operator should be on, preserving the current
 * env suffix (`digavouch.com` / `uat.digavouch.com` / `staging.digavouch.com`)
 * and swapping the leading label. Used by ProtectedRoute to redirect operators
 * to their membership org's subdomain (or `app` when they have no org).
 *
 *   buildCanonicalHost("rockwell.uat.digavouch.com", "paigekares")
 *     -> "paigekares.uat.digavouch.com"
 *   buildCanonicalHost("app.digavouch.com", "rockwell") -> "rockwell.digavouch.com"
 *
 * Returns null for non-canonical hosts (localhost, raw IPs, *.netlify.app,
 * *.lovable*, previews) — callers should NOT redirect there (dev/preview).
 */
export function buildCanonicalHost(
  hostname: string | null | undefined,
  label: string,
): string | null {
  if (!hostname || !label) return null;
  const host = hostname.toString().trim().toLowerCase().replace(/:\d+$/, "");
  // Leftmost-match picks the longest env suffix (uat/staging before bare).
  const m = host.match(/\.(staging\.digavouch\.com|uat\.digavouch\.com|digavouch\.com)$/);
  if (!m) return null;
  return `${label}.${m[1]}`;
}
